// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/store/modules/user'
import { usePermissionStore } from '@/store/modules/permission'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    redirect: '/inbox',
    children: [
      { path: 'inbox', name: 'Inbox', component: () => import('@/views/webmail/Inbox.vue'), meta: { title: '收件箱' } },
      { path: 'sent', name: 'Sent', component: () => import('@/views/webmail/Sent.vue'), meta: { title: '已发送' } },
      { path: 'drafts', name: 'Drafts', component: () => import('@/views/webmail/Drafts.vue'), meta: { title: '草稿箱' } },
      { path: 'trash', name: 'Trash', component: () => import('@/views/webmail/Trash.vue'), meta: { title: '垃圾箱' } },
      { path: 'compose', name: 'Compose', component: () => import('@/views/webmail/Compose.vue'), meta: { title: '写邮件' } },
      { path: 'detail/:folder/:uid', name: 'EmailDetail', component: () => import('@/views/webmail/Detail.vue'), meta: { title: '邮件详情' } },
      { path: 'forward-rules', name: 'ForwardRules', component: () => import('@/views/webmail/ForwardRules.vue'), meta: { title: '转发规则' } },
      { path: 'sort-rules', name: 'SortRules', component: () => import('@/views/webmail/SortRules.vue'), meta: { title: '分拣规则' } },
      { path: 'contacts', name: 'Contacts', component: () => import('@/views/webmail/Contacts.vue'), meta: { title: '通讯录' } },
      { path: 'signatures', name: 'Signatures', component: () => import('@/views/webmail/Signatures.vue'), meta: { title: '邮件签名' } },
      { path: 'templates', name: 'Templates', component: () => import('@/views/webmail/TemplateManager.vue'), meta: { title: '邮件模板' } },
    ],
  },
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    redirect: '/admin/account',
    meta: { requiresAdmin: true },
    children: [
      { path: 'account', name: 'AdminAccount', component: () => import('@/views/admin/AccountList.vue'), meta: { title: '账号管理' } },
      { path: 'domain', name: 'AdminDomain', component: () => import('@/views/admin/DomainList.vue'), meta: { title: '域名管理' } },
      { path: 'directmail', name: 'AdminDirectMail', component: () => import('@/views/admin/DirectMailList.vue'), meta: { title: '发信配置' } },
      { path: 'role', name: 'AdminRole', component: () => import('@/views/admin/RoleList.vue'), meta: { title: '角色管理' } },
      { path: 'department', name: 'AdminDepartment', component: () => import('@/views/admin/DepartmentList.vue'), meta: { title: '部门管理' } },
    ],
  },
  { path: '/403', name: 'Forbidden', component: () => import('@/views/error/403.vue'), meta: { public: true } },
  { path: '/404', name: 'NotFound', component: () => import('@/views/error/404.vue'), meta: { public: true } },
  { path: '/:pathMatch(.*)*', redirect: '/404' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  // 1. Public pages pass through
  if (to.meta.public) return next()

  const userStore = useUserStore()

  // 2. Check token
  if (!userStore.token) return next('/login')

  // 3. Check user info (restore on page refresh)
  if (!userStore.userInfo?.email) {
    const saved = localStorage.getItem('userInfo')
    if (saved) {
      try {
        userStore.userInfo = JSON.parse(saved)
        userStore.roles = JSON.parse(localStorage.getItem('roles') || '[]')
        userStore.permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      } catch (e) {}
    }
    if (!userStore.userInfo?.email) {
      const ok = await userStore.fetchProfile()
      if (!ok) return next('/login')
    }
  }

  // 4. Load menus (once)
  const permissionStore = usePermissionStore()
  if (!permissionStore.routesLoaded) {
    await permissionStore.fetchMenus()
    permissionStore.setPermissions(userStore.permissions)
    permissionStore.routesLoaded = true
  }

  // 5. Admin route check
  if (to.meta.requiresAdmin && !userStore.isAdmin) {
    return next('/403')
  }

  next()
})

export default router
